/**
 * Calculate academic year from admission date
 * Academic year runs from April to March
 * @param {string} dateString - Admission date (format: YYYY-MM-DD or any valid date format)
 * @returns {string} - Academic year (e.g., "2024-2025")
 */
export const getAcademicYear = (dateString) => {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = date.getMonth() + 1; // 0-indexed, so +1

    // Academic year starts in April (month 4)
    // Apr-Dec -> belongs to current year's academic year start
    // Jan-Mar -> belongs to previous year's academic year start
    if (month >= 4) {
      return `${year}-${(year + 1).toString().slice(-2)}`;
    } else {
      return `${year - 1}-${year.toString().slice(-2)}`;
    }
  } catch (error) {
    console.error("Error calculating academic year:", error);
    return '';
  }
};

/**
 * Get the next academic year
 * @param {string} currentAcademicYear - Current academic year (e.g., "2024-2025")
 * @returns {string} - Next academic year (e.g., "2025-2026")
 */
export const getNextAcademicYear = (currentAcademicYear) => {
  if (!currentAcademicYear) return '';
  
  try {
    const [startYear] = currentAcademicYear.split('-');
    const year = parseInt(startYear, 10);
    return `${year + 1}-${(year + 2).toString().slice(-2)}`;
  } catch (error) {
    console.error("Error calculating next academic year:", error);
    return '';
  }
};

/**
 * Generates a list of academic years.
 * @param {number} numPastYears - Number of past academic years to generate (including current).
 * @param {number} numFutureYears - Number of future academic years to generate.
 * @returns {string[]} - An array of academic year strings (e.g., ["2023-2024", "2024-2025"]).
 */
export const generateAcademicYears = (numPastYears = 5, numFutureYears = 1) => {
  const currentFullDate = new Date();
  const currentYear = currentFullDate.getFullYear();
  const currentMonth = currentFullDate.getMonth() + 1; // 1-indexed

  let startYearForCurrentAcademicYear;

  if (currentMonth >= 4) { // April to December
    startYearForCurrentAcademicYear = currentYear;
  } else { // January to March
    startYearForCurrentAcademicYear = currentYear - 1;
  }

  const academicYears = [];

  // Generate past academic years
  for (let i = numPastYears - 1; i >= 0; i--) {
    const start = startYearForCurrentAcademicYear - i;
    academicYears.push(`${start}-${(start + 1).toString().slice(-2)}`);
  }

  // Generate future academic years
  for (let i = 1; i <= numFutureYears; i++) {
    const start = startYearForCurrentAcademicYear + i;
    academicYears.push(`${start}-${(start + 1).toString().slice(-2)}`);
  }

  return academicYears;
};
